import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Mail, Lock, User, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/brand";

const EUCALYPTUS = "https://images.pexels.com/photos/8903651/pexels-photo-8903651.jpeg";

export default function Auth({ mode = "login" }) {
  const isRegister = mode === "register";
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isRegister) await register(email, password, name);
      else await login(email, password);
      navigate("/planner");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/planner";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-6 relative overflow-hidden">
      <div className="eucalyptus-corner top-0 left-0" style={{ backgroundImage: `url(${EUCALYPTUS})`, transform: "scaleX(-1)" }} />
      <div className="eucalyptus-corner bottom-0 right-0" style={{ backgroundImage: `url(${EUCALYPTUS})` }} />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white border border-gold/25 rounded-md p-10 shadow-[0_20px_50px_rgba(27,42,71,0.10)] relative z-10">
        <button onClick={() => navigate("/")} className="flex items-center gap-1 text-sm text-navy/60 hover:text-navy transition-colors mb-6" data-testid="auth-back-btn">
          <ArrowLeft className="w-4 h-4" /> Home
        </button>

        <div className="flex items-center gap-3 mb-2">
          <img src={LOGO_URL} alt="The Ever Grace Group" className="h-12 w-12 rounded-full object-cover" />
          <span className="font-serif text-2xl text-navy"><span className="text-navy">Ever</span> <span className="text-gold">Grace</span></span>
        </div>
        <h1 className="font-serif text-4xl text-navy mb-2" data-testid="auth-title">
          {isRegister ? "Create your account" : "Welcome back"}
        </h1>
        <p className="text-charcoal/70 text-sm mb-8">
          {isRegister ? "Save your progress and return anytime." : "Sign in to continue where you left off."}
        </p>

        <Button onClick={googleLogin} variant="outline" data-testid="google-login-btn"
          className="w-full mb-6 border-navy/20 text-navy hover:bg-navy/5 rounded-md py-6">
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <span className="h-px flex-1 bg-gold/20" />
          <span className="text-xs text-charcoal/50 uppercase tracking-wider">or</span>
          <span className="h-px flex-1 bg-gold/20" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {isRegister && (
            <div>
              <Label htmlFor="name" className="text-navy text-sm font-semibold">Name</Label>
              <div className="relative mt-1">
                <User className="w-4 h-4 text-sage absolute left-3 top-1/2 -translate-y-1/2" />
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name"
                  placeholder="Your name" className="pl-9 bg-ivory border-gold/25 focus-visible:ring-sage" />
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="email" className="text-navy text-sm font-semibold">Email</Label>
            <div className="relative mt-1">
              <Mail className="w-4 h-4 text-sage absolute left-3 top-1/2 -translate-y-1/2" />
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email" placeholder="you@example.com" className="pl-9 bg-ivory border-gold/25 focus-visible:ring-sage" />
            </div>
          </div>
          <div>
            <Label htmlFor="password" className="text-navy text-sm font-semibold">Password</Label>
            <div className="relative mt-1">
              <Lock className="w-4 h-4 text-sage absolute left-3 top-1/2 -translate-y-1/2" />
              <Input id="password" type="password" required minLength={isRegister ? 6 : undefined} value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password" placeholder="••••••••" className="pl-9 bg-ivory border-gold/25 focus-visible:ring-sage" />
            </div>
          </div>

          {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

          <Button type="submit" disabled={busy} data-testid="auth-submit-btn"
            className="w-full bg-navy hover:bg-navy/90 text-ivory rounded-md py-6 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {isRegister ? "Create account" : "Sign in"}
          </Button>
        </form>

        <p className="text-sm text-charcoal/70 text-center mt-6">
          {isRegister ? (
            <>Already have an account? <Link to="/login" className="text-gold hover:underline" data-testid="switch-login">Sign in</Link></>
          ) : (
            <>New here? <Link to="/register" className="text-gold hover:underline" data-testid="switch-register">Create an account</Link></>
          )}
        </p>
      </motion.div>
    </div>
  );
}
